import {
  DOCUMENT_EXPIRY_WARNING_DAYS,
  DOCUMENT_READING_LABEL,
  type Matter,
  type MatterDocument,
} from '@buakei/types';
import { Button, Notice } from '@buakei/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileUp, FlaskConical, RotateCcw, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import type { useReaderTokens } from '@/pages/library/LibraryPages';
import { api, draftingApi } from '@/services/api';
import { PDF_FILE, saveFile, WORD_FILE, type FileKind } from '@/services/save-file';

/**
 * Client documents on a matter (ADR-044). Shown only when uploads are switched on for a firm
 * matter. The people who may open the matter see its documents; Buakei reads them in parts when
 * analysing and drafting, as the client's evidence, never as legal authority.
 */

const TEXT_FILE: FileKind = { extension: '.txt', mime: 'text/plain', description: 'Text file' };
const FILE_KIND = { pdf: PDF_FILE, docx: WORD_FILE, text: TEXT_FILE } as const;
const ACCEPT = '.pdf,.docx,.txt,application/pdf,text/plain';

const size = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

export function MatterDocuments({
  tokens,
  workspaceId,
  matter,
}: {
  tokens: NonNullable<ReturnType<typeof useReaderTokens>>;
  workspaceId: string;
  matter: Matter;
}) {
  const queryClient = useQueryClient();
  // Until the ADR-044 gates pass, uploads run as a test with made-up documents only.
  const flags = useQuery({ queryKey: ['feature-flags'], queryFn: api.featureFlags });
  const key = ['matter-documents', workspaceId, matter.id];
  const list = useQuery({
    queryKey: key,
    queryFn: () => draftingApi.documents(tokens, workspaceId, matter.id),
  });
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  const upload = useMutation({
    mutationFn: async (chosen: File[]) => {
      const max = list.data?.maxBytes ?? 0;
      for (const [i, file] of chosen.entries()) {
        if (max && file.size > max) throw new Error(`${file.name} is larger than 50 MB.`);
        setProgress(`Adding ${i + 1} of ${chosen.length}: ${file.name}…`);
        await draftingApi.uploadDocument(tokens, workspaceId, matter.id, file);
      }
    },
    onSettled: () => {
      setProgress(null);
      if (input.current) input.current.value = '';
      void refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (doc: string) => draftingApi.removeDocument(tokens, workspaceId, matter.id, doc),
    onSettled: () => {
      setConfirming(null);
      void refresh();
    },
  });
  const retry = useMutation({
    mutationFn: (doc: string) => draftingApi.readDocument(tokens, workspaceId, matter.id, doc),
    onSettled: () => void refresh(),
  });
  const download = useMutation({
    mutationFn: async (doc: MatterDocument) =>
      saveFile(
        await draftingApi.downloadDocument(tokens, workspaceId, matter.id, doc),
        doc.name.replace(/\.[^.]+$/, ''),
        FILE_KIND[doc.kind],
      ),
  });

  const docs = list.data?.documents ?? [];
  const full = list.data ? docs.length >= list.data.limit : false;
  const error = upload.error ?? remove.error ?? retry.error ?? download.error;
  const soon = Date.now() + DOCUMENT_EXPIRY_WARNING_DAYS * 86_400_000;

  return (
    <section aria-labelledby="documents-h" className="flex flex-col gap-3">
      <h2 id="documents-h" className="font-serif text-xl font-semibold text-navy-900">
        Client documents
      </h2>
      <p className="text-sm text-ink-900">
        Add the papers in this matter: PDF, Word (.docx) or text, up to 50 MB each and{' '}
        {list.data?.limit ?? 30} per matter. They stay private to this matter
        {matter.sharedWithWorkspace ? ' and the colleagues it is shared with' : ''}. Buakei reads
        them when analysing and drafting, as the client’s evidence and never as legal authority.
        They are deleted 24 months after the matter last changes.
      </p>
      {flags.data?.documentUploads !== 'live' ? (
        <Notice title="Testing only: use made-up documents" icon={FlaskConical}>
          <p>
            Uploads are switched on for testing before the confidentiality checks are complete. Do
            not add real client documents or anything confidential.
          </p>
        </Notice>
      ) : null}

      {list.isLoading ? <p className="text-sm text-muted-600">Loading documents…</p> : null}
      {list.isError ? (
        <p role="alert" className="text-sm text-ink-900">
          {list.error.message}
        </p>
      ) : null}
      {list.data && docs.length === 0 ? (
        <p className="text-sm text-muted-600">No documents yet.</p>
      ) : null}

      {docs.length > 0 ? (
        <ul className="flex flex-col gap-2" aria-label="Client documents">
          {docs.map((d) => (
            <li
              key={d.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-line-200 bg-white p-3"
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-medium text-navy-900">{d.name}</span>
                <span className="text-xs text-muted-600">
                  {size(d.size)}
                  {d.pages !== null
                    ? ` · ${d.pages} ${d.pages === 1 ? 'page' : 'pages'}`
                    : ''} · {DOCUMENT_READING_LABEL[d.reading]}
                  {d.uploadedByName ? ` · added by ${d.uploadedByName}` : ''}
                </span>
                {Date.parse(d.expiresAt) <= soon ? (
                  <span className="text-xs text-ink-900">
                    Will be deleted on {day(d.expiresAt)} unless the matter changes.
                  </span>
                ) : null}
              </div>
              {d.reading === 'failed' || d.reading === 'pending' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Read again: ${d.name}`}
                  disabled={retry.isPending}
                  onClick={() => retry.mutate(d.id)}
                >
                  <RotateCcw className="size-4" aria-hidden /> Read again
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Download ${d.name}`}
                disabled={download.isPending}
                onClick={() => download.mutate(d)}
              >
                <Download className="size-4" aria-hidden /> Download
              </Button>
              {d.canDelete ? (
                confirming === d.id ? (
                  <span className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(d.id)}
                    >
                      Delete permanently
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
                      Keep
                    </Button>
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Delete ${d.name}`}
                    onClick={() => setConfirming(d.id)}
                  >
                    <Trash2 className="size-4" aria-hidden /> Delete
                  </Button>
                )
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={input}
          id="matter-document-input"
          type="file"
          multiple
          accept={ACCEPT}
          aria-label="Choose documents to add"
          tabIndex={-1}
          className="sr-only"
          onChange={(e) => {
            const chosen = [...(e.target.files ?? [])];
            if (chosen.length) upload.mutate(chosen);
          }}
        />
        <Button
          variant="secondary"
          disabled={upload.isPending || full || !list.data}
          onClick={() => input.current?.click()}
        >
          <FileUp className="size-4" aria-hidden />
          {progress ?? 'Add documents'}
        </Button>
        {full ? (
          <span className="text-sm text-ink-900">
            This matter has the most documents allowed. Delete one to add another.
          </span>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-ink-900">
          {error.message}
        </p>
      ) : null}
    </section>
  );
}
